import type { SanityDocument } from '@sanity/client';

import type { StudioPathLike } from '@sanity/client/csm';
import cn from 'classnames';
import type { ComponentType } from 'react';
import { rememberScrollPositionOnClick, useHandleScrollFromHistory } from '../../utils/scrollHistory';
import useDataAttribute, { ScopeDataAttribute } from '../infrastructure/DataAttributeContext';
import * as style from './Grid.module.css';

interface GridProps<
    T extends
        | SanityDocument
        | {
              _key: string;
              _id?: never;
          },
> {
    items: T[];
    component: ComponentType<T>;
    gridMode?: 'auto-fit' | 'auto-fil' | 'single-column';
    studioPath?: StudioPathLike;
}

export function Grid<T extends SanityDocument | { _key: string; _id?: never }>({ items, component: Component, gridMode, studioPath }: GridProps<T>) {
    const dataAttr = useDataAttribute();

    useHandleScrollFromHistory();

    return (
        <ul
            className={cn(style.list, gridMode && style[gridMode])}
            data-sanity={studioPath && dataAttr?.(studioPath)}
            onClick={rememberScrollPositionOnClick}
        >
            <ScopeDataAttribute path={studioPath ?? []}>
                {items?.filter(Boolean).map((d) => (
                    <ScopeDataAttribute key={d._id ?? d._key} {...(d._id ? { _id: d._id, _type: d._type } : { _key: d._key })}>
                        <li data-sanity={studioPath && d._key && dataAttr?.scope(studioPath)([{ _key: d._key }])}>
                            <Component {...d} />
                        </li>
                    </ScopeDataAttribute>
                ))}
            </ScopeDataAttribute>
        </ul>
    );
}
